import { Component, inject, OnInit, ViewChild, ElementRef } from "@angular/core";
import { AG_GRID_LOCALE_DE } from "../../_helpers/aggrid.locale.de";
import {
  GridApi,
  SideBarDef,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
  SizeColumnsToContentStrategy,
} from "ag-grid-enterprise";
import { ColDef, GridReadyEvent, ValueFormatterParams, CellClickedEvent } from "ag-grid-community";
import { AgGridAngular } from "ag-grid-angular";
import { AgGridThemeService } from "../../_services/ag-grid-theme.service";
import { RecordNavigationComponent } from "../_shared/record-navigation/record-navigation.component";
import { ExpectedDeliveryService } from "../../_services/expected-delivery.service";
import { QuickCapturedDeliveryService } from "../../_services/quick-captured-delivery.service";
import { ExpectedDelivery } from "../../_models/expected-delivery.interface";
import { QuickCapturedDelivery } from "../../_models/quick-captured-delivery.interface";
import { RouterLink } from "@angular/router";

import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

@Component({
  selector: "hawk-expected-deliveries-index",
  templateUrl: "./expected-deliveries-index.component.html",
  standalone: true,
  imports: [AgGridAngular, RecordNavigationComponent, RouterLink],
  host: {
    class: "app-page",
  },
})
export class ExpectedDeliveriesIndexComponent implements OnInit {
  @ViewChild("quickFilterInput") quickFilterInput!: ElementRef<HTMLInputElement>;

  public hawkTheme = inject(AgGridThemeService).getTheme();
  public expectedDeliveries: ExpectedDelivery[] = [];
  public quickCapturedDeliveries: QuickCapturedDelivery[] = [];
  public unassignedQuickCapturedCount = 0;
  protected readonly locale = AG_GRID_LOCALE_DE;
  private gridApi!: GridApi;
  private quickCapturedGridApi!: GridApi;
  private quickCapturedDeliveryService = inject(QuickCapturedDeliveryService);
  private sanitizer = inject(DomSanitizer);

  // Assignment Mode
  public assignmentMode = false;
  public selectedQuickCaptured: QuickCapturedDelivery | null = null;

  // PDF Viewer
  public pdfViewerUrl: SafeResourceUrl | null = null;
  public pdfViewerTitle = "";

  /**
   * Inline-Styles für Zeilen basierend auf Status
   */
  public getRowStyle = (params: any) => {
    if (params.data?.status === "unexpected") {
      return { backgroundColor: "rgba(255, 165, 0, 0.15)" }; // Orange für unexpected (wartet auf Freigabe)
    }
    if (params.data?.status === "pending_confirmation") {
      return { backgroundColor: "rgba(59, 130, 246, 0.1)" }; // Blau für pending_confirmation (wartet auf Lager-Bestätigung)
    }
    if (params.data?.status === "completed") {
      return { backgroundColor: "rgba(0, 200, 83, 0.1)" }; // Grün für completed
    }
    if (params.data?.status === "cancelled") {
      return { backgroundColor: "rgba(0, 0, 0, 0.05)", opacity: "0.7" }; // Grau für cancelled
    }
    if (params.data?.status === "partial") {
      return { backgroundColor: "rgba(59, 130, 246, 0.1)" }; // Blau für partial
    }
    return undefined;
  };

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
    defaultMinWidth: 80,
  };

  // Base column definitions für ExpectedDeliveries
  private baseColDefs: ColDef[] = [
    {
      field: "order_nr",
      headerName: "Auftrags-Nr.",
      width: 150,
      cellRenderer: this.orderNrRenderer,
    },
    {
      field: "supplier_name",
      headerName: "Lieferant",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      cellRenderer: this.statusRenderer,
    },
    {
      field: "status",
      headerName: "Freigabe",
      width: 150,
      cellRenderer: this.approvalStatusRenderer,
    },
    {
      field: "project_name",
      headerName: "Projekt",
      width: 180,
    },
    {
      field: "items_count",
      headerName: "Positionen",
      width: 100,
      cellClass: "text-right",
      headerClass: "ag-right-aligned-header",
    },
    {
      field: "total_ordered",
      headerName: "Soll",
      width: 80,
      cellClass: "text-right",
      headerClass: "ag-right-aligned-header",
    },
    {
      field: "total_received",
      headerName: "Ist",
      width: 80,
      cellClass: "text-right",
      headerClass: "ag-right-aligned-header",
    },
    {
      field: "progress_percentage",
      headerName: "Fortschritt",
      width: 120,
      cellRenderer: this.progressRenderer,
    },
    {
      field: "expected_until",
      headerName: "Erwartet bis",
      width: 120,
      valueFormatter: this.dateFormatter,
    },
    {
      field: "created_by_name",
      headerName: "Erstellt von",
      width: 150,
    },
    {
      field: "created_at",
      headerName: "Erstellt am",
      width: 120,
      valueFormatter: this.dateFormatter,
    },
  ];

  // Dynamic column definitions (adds "Auswählen" column in assignment mode)
  public get colDefs(): ColDef[] {
    if (this.assignmentMode) {
      return [
        ...this.baseColDefs,
        {
          headerName: "Auswählen",
          width: 150,
          minWidth: 150,
          pinned: "right",
          suppressSizeToFit: true,
          cellRenderer: () => `<button class="bm-btn bm-btn-sm bm-btn-primary">Auswählen</button>`,
          onCellClicked: (event: CellClickedEvent) => this.onSelectDeliveryForAssignment(event.data),
        },
      ];
    }
    return this.baseColDefs;
  }

  // Column definitions für QuickCapturedDeliveries
  public quickCapturedColDefs: ColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 60,
    },
    {
      field: "supplier_name",
      headerName: "Lieferant",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params: any) => params.value || "-",
    },
    {
      field: "packaging_type",
      headerName: "Lieferform",
      width: 100,
      cellRenderer: this.packagingTypeRenderer,
    },
    {
      field: "tracking_numbers",
      headerName: "Tracking",
      width: 150,
      cellRenderer: this.trackingRenderer,
    },
    {
      field: "notes",
      headerName: "Hinweise",
      width: 150,
      valueFormatter: (params: any) => params.value || "-",
    },
    {
      field: "assigned",
      headerName: "Status",
      width: 130,
      cellRenderer: this.assignedStatusRenderer,
    },
    {
      field: "created_by_name",
      headerName: "Erstellt von",
      width: 120,
      valueFormatter: (params: any) => params.value || "-",
    },
    {
      field: "created_at",
      headerName: "Erstellt am",
      width: 110,
      valueFormatter: this.dateFormatter,
    },
    {
      headerName: "Aktionen",
      width: 180,
      minWidth: 180,
      pinned: "right",
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        const pdfBtn = params.data?.pdf_exists
          ? `<button class="bm-btn bm-btn-sm bm-btn-ghost text-info" data-action="pdf">PDF</button>`
          : "";
        const assignBtn = !params.data?.assigned
          ? `<button class="bm-btn bm-btn-sm bm-btn-primary" data-action="assign">Zuordnen</button>`
          : "";
        return `<div class="flex gap-1 justify-end">${pdfBtn}${assignBtn}</div>`;
      },
      onCellClicked: (event: CellClickedEvent) => {
        const target = event.event?.target as HTMLElement;
        const action = target?.getAttribute("data-action");
        if (action === "pdf") {
          this.onViewPdf(event.data);
        } else if (action === "assign") {
          this.startAssignment(event.data);
        }
      },
    },
  ];

  public sideBarOptions: SideBarDef = {
    hiddenByDefault: true,
    toolPanels: ["columns", "filters"],
  };

  public defaultColDef: ColDef = {
    filter: true,
    sortable: true,
    resizable: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: false,
  };

  public rowSelection: "single" | "multiple" = "single";

  constructor(private expectedDeliveryService: ExpectedDeliveryService) {}

  ngOnInit(): void {
    this.loadData();
    this.loadQuickCapturedDeliveries();
  }

  loadData(): void {
    this.expectedDeliveryService.getAll().subscribe({
      next: (data) => {
        this.expectedDeliveries = data;
      },
      error: (err) => {
        console.error("Fehler beim Laden der erwarteten Lieferungen:", err);
      },
    });
  }

  loadQuickCapturedDeliveries(): void {
    this.quickCapturedDeliveryService.getAll(false).subscribe({
      next: (response) => {
        this.quickCapturedDeliveries = response.deliveries;
        this.unassignedQuickCapturedCount = response.unassigned_count;
      },
      error: (err) => {
        console.error("Fehler beim Laden der Schnellerfassungen:", err);
      },
    });
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  onQuickCapturedGridReady(params: GridReadyEvent): void {
    this.quickCapturedGridApi = params.api;
  }

  public quickSearchChanged(e: Event): void {
    const newValue = (e.target as HTMLInputElement).value;
    this.gridApi.setGridOption("quickFilterText", newValue);
  }

  orderNrRenderer(params: any): string {
    const id = params.data?.id;
    const orderNr = params.value || "-";
    return `<a class="bm-link bm-link-hover font-medium" href="/a/lager/erwartete-lieferungen/${id}" rel="noopener">${orderNr}</a>`;
  }

  statusRenderer(params: any): string {
    const status = params.value;
    const statusMap: Record<string, { label: string; class: string }> = {
      open: { label: "Offen", class: "badge-info" },
      partial: { label: "Teilweise", class: "badge-warning" },
      completed: { label: "Abgeschlossen", class: "badge-success" },
      cancelled: { label: "Storniert", class: "badge-error" },
      unexpected: { label: "Unerwartet", class: "" },
      pending_confirmation: { label: "Einbuchung ausstehend", class: "" },
    };
    const statusInfo = statusMap[status] || { label: status, class: "badge-ghost" };
    return `<span class="badge ${statusInfo.class} badge-sm">${statusInfo.label}</span>`;
  }

  approvalStatusRenderer(params: any): string {
    const status = params.value;
    if (status === "unexpected") {
      return `<span class="badge badge-warning badge-sm animate-pulse">Wartet auf Freigabe</span>`;
    }
    return `<span class="badge badge-success badge-sm">Freigegeben</span>`;
  }

  progressRenderer(params: any): string {
    const percentage = params.value || 0;
    const colorClass = percentage >= 100 ? "progress-success" : percentage > 0 ? "progress-warning" : "progress-info";
    return `
      <div class="flex items-center gap-2">
        <progress class="progress ${colorClass} w-16 h-2" value="${percentage}" max="100"></progress>
        <span class="text-xs">${percentage}%</span>
      </div>
    `;
  }

  dateFormatter(params: ValueFormatterParams): string {
    if (!params.value) return "";
    const date = new Date(params.value);
    return date.toLocaleDateString("de-DE");
  }

  // Renderers für QuickCapturedDeliveries

  quickCapturedIdRenderer(params: any): string {
    const id = params.value;
    return `<a class="bm-link bm-link-hover font-medium" href="/a/lager/schnellerfassungen/${id}" rel="noopener">#${id}</a>`;
  }

  packagingTypeRenderer(params: any): string {
    const type = params.value;
    if (type === "PALLET") {
      return '<span title="Palette">🪵 Palette</span>';
    } else if (type === "PACKAGE") {
      return '<span title="Paket">📦 Paket</span>';
    }
    return "-";
  }

  trackingRenderer(params: any): string {
    const tracking = params.value;
    if (!tracking || tracking.length === 0) {
      return "-";
    }
    const first = tracking[0];
    const more =
      tracking.length > 1 ? ` <span class="text-xs text-base-content/60">+${tracking.length - 1}</span>` : "";
    return `<span class="text-xs">${first.carrier}: ${first.number}${more}</span>`;
  }

  assignedStatusRenderer(params: any): string {
    const assigned = params.value;
    if (assigned) {
      return `<span class="badge badge-success badge-sm">Zugeordnet</span>`;
    }
    return `<span class="badge badge-warning badge-sm">Nicht zugeordnet</span>`;
  }

  pdfExistsRenderer(params: any): string {
    const exists = params.value;
    if (exists) {
      return `<span class="text-success">✓</span>`;
    }
    return `<span class="text-error">✗</span>`;
  }

  // === Assignment Mode Methods ===

  /**
   * Startet den Zuordnungs-Modus für eine Schnellerfassung
   */
  startAssignment(quickCaptured: QuickCapturedDelivery): void {
    this.assignmentMode = true;
    this.selectedQuickCaptured = quickCaptured;

    // Filter mit Lieferant vorausfüllen
    if (quickCaptured.supplier_name && this.quickFilterInput) {
      this.quickFilterInput.nativeElement.value = quickCaptured.supplier_name;
      this.gridApi?.setGridOption("quickFilterText", quickCaptured.supplier_name);
    }

    // Grid-Spalten aktualisieren
    this.gridApi?.setGridOption("columnDefs", this.colDefs);
  }

  /**
   * Bricht den Zuordnungs-Modus ab
   */
  cancelAssignment(): void {
    this.assignmentMode = false;
    this.selectedQuickCaptured = null;

    // Filter zurücksetzen
    if (this.quickFilterInput) {
      this.quickFilterInput.nativeElement.value = "";
      this.gridApi?.setGridOption("quickFilterText", "");
    }

    // Grid-Spalten aktualisieren
    this.gridApi?.setGridOption("columnDefs", this.colDefs);
  }

  /**
   * Wird aufgerufen wenn eine Lieferung im Zuordnungs-Modus ausgewählt wird
   */
  onSelectDeliveryForAssignment(delivery: ExpectedDelivery): void {
    if (!this.selectedQuickCaptured) return;

    // Nur offene oder teilweise eingebuchte Lieferungen erlauben
    if (!["open", "partial", "unexpected"].includes(delivery.status)) {
      alert("Diese Lieferung kann nicht zugeordnet werden (Status: " + delivery.status + ")");
      return;
    }

    const qc = this.selectedQuickCaptured;
    const message = `Möchten Sie die Schnellerfassung #${qc.id}${qc.supplier_name ? " (" + qc.supplier_name + ")" : ""} der Lieferung "${delivery.order_nr}" (${delivery.supplier_name}) zuordnen?`;

    if (confirm(message)) {
      this.confirmAssignment(qc.id, delivery.id);
    }
  }

  /**
   * Führt die Zuordnung durch
   */
  private confirmAssignment(quickCapturedId: number, expectedDeliveryId: number): void {
    this.quickCapturedDeliveryService.assignToExisting(quickCapturedId, expectedDeliveryId).subscribe({
      next: (response) => {
        if (response.success) {
          alert("Zuordnung erfolgreich!");
          this.cancelAssignment();
          this.loadQuickCapturedDeliveries();
          this.loadData();
        } else {
          alert("Fehler bei der Zuordnung: " + (response.error || "Unbekannter Fehler"));
        }
      },
      error: (err) => {
        console.error("Fehler bei der Zuordnung:", err);
        alert("Fehler bei der Zuordnung: " + (err.error?.error || err.message || "Unbekannter Fehler"));
      },
    });
  }

  // === PDF Viewer Methods ===

  /**
   * Öffnet den PDF-Viewer für eine Schnellerfassung
   */
  onViewPdf(quickCaptured: QuickCapturedDelivery): void {
    if (!quickCaptured.pdf_exists) return;

    const url = this.quickCapturedDeliveryService.getPdfDownloadUrl(quickCaptured.id);
    this.pdfViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.pdfViewerTitle = quickCaptured.supplier_name || `Schnellerfassung #${quickCaptured.id}`;
  }

  /**
   * Schließt den PDF-Viewer
   */
  closePdfViewer(): void {
    this.pdfViewerUrl = null;
    this.pdfViewerTitle = "";
  }
}
